import React, { FC, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import DeleteDialog from './DeleteDialog';
import ReportDialog from './ReportDialog';
import { removePost, hidePost, reportPost } from '../reducers/postReducer';
import { notify } from '../reducers/notificationReducer';
import getErrorMsg from '../utils/getErrorMsg';
import posthog from '../utils/posthog';
import { IconButton, Menu, MenuItem, ListItemIcon, Divider } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';

interface PostContextMenuProps {
  id: string;
  title: string;
  /** The post's author id, so the menu knows which actions to offer. */
  authorId?: string;
  buttonType?: 'menu' | 'buttonGroup';
}

/**
 * The post "…" menu, for every signed-in reader rather than only the author.
 *
 * Two tiers: anyone logged in can hide or report a post; the author also gets
 * edit and delete. Signed-out readers get no menu at all — every action here
 * writes to an account.
 *
 * This replaces EditDeleteMenu, which was mounted only when
 * `user.id === author.id`; that ownership check now lives inside, because the
 * menu itself is no longer owner-only.
 */
const PostContextMenu: FC<PostContextMenuProps> = ({ id, title, authorId, buttonType = 'menu' }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppSelector((state) => state.user);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [reportOpen, setReportOpen] = useState(false);

  // Every action writes to an account, so there is nothing to offer a guest.
  if (!user) return null;

  const isAuthor = Boolean(authorId && user.id === authorId);
  const handleClose = () => setAnchorEl(null);

  const handleEdit = () => {
    handleClose();
    navigate(`/submit/${id}`);
  };

  const handleDeletePost = async () => {
    try {
      handleClose();
      await dispatch(removePost(id));
      posthog.capture('post_deleted', { post_id: id });
      if (location.pathname !== '/') {
        navigate('/');
      }
      dispatch(notify('Post deleted!', 'success'));
    } catch (err) {
      dispatch(notify(getErrorMsg(err), 'error'));
    }
  };

  const handleHide = async () => {
    handleClose();
    try {
      await dispatch(hidePost(id));
      posthog.capture('post_hidden', { post_id: id });
      dispatch(notify('Post hidden from your feeds.', 'success'));
      // On the post's own page there is nothing left to look at.
      if (location.pathname.startsWith('/comments/')) navigate('/');
    } catch (err) {
      dispatch(notify(getErrorMsg(err), 'error'));
    }
  };

  const handleReport = async (reason: string, detail?: string) => {
    try {
      await dispatch(reportPost(id, reason, detail));
      posthog.capture('post_reported', { post_id: id, reason });
      dispatch(notify('Thanks — we\'ll take a look. The post is hidden from your feeds.', 'success'));
      if (location.pathname.startsWith('/comments/')) navigate('/');
    } catch (err) {
      dispatch(notify(getErrorMsg(err), 'error'));
    }
  };

  const hideItem = (
    <MenuItem key="hide" onClick={handleHide}>
      <ListItemIcon>
        <VisibilityOffIcon style={{ marginRight: 'var(--space-icon-gap)' }} /> Hide Post
      </ListItemIcon>
    </MenuItem>
  );

  const reportItem = (
    <MenuItem
      key="report"
      onClick={() => {
        handleClose();
        setReportOpen(true);
      }}
    >
      <ListItemIcon>
        <FlagOutlinedIcon style={{ marginRight: 'var(--space-icon-gap)' }} /> Report Post
      </ListItemIcon>
    </MenuItem>
  );

  const editItem = (
    <MenuItem key="edit" onClick={handleEdit}>
      <ListItemIcon>
        <EditIcon style={{ marginRight: 'var(--space-icon-gap)' }} /> Edit Post
      </ListItemIcon>
    </MenuItem>
  );

  // Reporting your own post is refused server-side, so don't offer it.
  // Edit/Delete appear inline in buttonGroup mode, in the menu otherwise —
  // Hide and Report always live in the menu, for author and reader alike.
  const overflowItems = isAuthor ? [hideItem] : [hideItem, reportItem];
  const menuItems =
    isAuthor && buttonType === 'menu'
      ? [
          editItem,
          <DeleteDialog
            key="delete"
            title={title}
            handleDelete={handleDeletePost}
            handleMenuClose={handleClose}
          />,
          <Divider key="div" />,
          ...overflowItems,
        ]
      : overflowItems;

  const overflowMenu = (
    <>
      <IconButton
        onClick={(e) => setAnchorEl(e.currentTarget)}
        size="small"
        aria-label="Post options"
        sx={{ color: 'var(--color-text-3)', '&:hover': { color: 'text.primary' } }}
      >
        <MoreHorizIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
        {menuItems}
      </Menu>
      <ReportDialog open={reportOpen} onClose={() => setReportOpen(false)} onSubmit={handleReport} />
    </>
  );

  if (buttonType === 'buttonGroup') {
    // The post page shows the author's own controls inline, with everything
    // else behind the same overflow menu the cards use.
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {isAuthor && editItem}
        {isAuthor && (
          <DeleteDialog title={title} handleDelete={handleDeletePost} handleMenuClose={handleClose} />
        )}
        {overflowMenu}
      </div>
    );
  }

  return <div>{overflowMenu}</div>;
};

export default PostContextMenu;
